import { AppStrings } from "../../../../utils/constants/AppStrings";
import { AppColors } from "../../../../utils/AppColors";

import { isValidHex } from "./colorPickerPalette";

/**
 * The colour palette the Game Pass Style → Color section is allowed to edit.
 *
 * The colours API (`common_assets/colors.json`) returns the *entire* platform
 * palette — sportsbook, poker, casino, pikkem, p2p, etc. ("other model
 * colours"). This screen only surfaces the keys that trivelta-gamepass-frontend
 * actually consumes: the core brand tokens its MUI theme picks out
 * (`src/lib/theme/createTheme.ts`) plus every `GAMEPASS_*` / `GAME_PASS_*`
 * token. The order here is the order the rows render in.
 *
 * `label` overrides the auto-derived title for the handful of slots the Figma
 * names explicitly (Theme Primary/Secondary, Text Primary/Secondary); every
 * other row's title is humanised from its key via {@link humanizeColorKey}.
 */
export type GamePassColorKey = {
  key: string;
  label?: string;
};

export const GAME_PASS_COLOR_KEYS: GamePassColorKey[] = [
  { key: "PRIMARY", label: AppStrings.GAME_PASS_STYLE_COLOR_THEME_PRIMARY },
  {
    key: "SECONDARY",
    label: AppStrings.GAME_PASS_STYLE_COLOR_THEME_SECONDARY,
  },
  {
    key: "LIGHT_TEXT_COLOR",
    label: AppStrings.GAME_PASS_STYLE_COLOR_TEXT_PRIMARY,
  },
  {
    key: "TEXT_SECONDARY_COLOR",
    label: AppStrings.GAME_PASS_STYLE_COLOR_TEXT_SECONDARY,
  },
  { key: "PRIMARY_BACKGROUND_COLOR" },
  { key: "MODAL_BACKGROUND" },
  { key: "PRIMARY_BUTTON" },
  {
    key: "GAMEPASS_COMPLETED_TASK_COLOR",
    label: AppStrings.GAME_PASS_STYLE_COLOR_COMPLETED_ACCENT,
  },
  {
    key: "COMPLETE_CHALLENGE_BTN_SHADOW",
    label: AppStrings.GAME_PASS_STYLE_COLOR_COMPLETED_BADGE_BG,
  },
  { key: "GAMEPASS_ACTIVE_COLOR" },
  { key: "GAMEPASS_PROGRESS_COLOR" },
  { key: "GAMEPASS_DIVIDER_GRAD_END_COLOR" },
  { key: "GAMEPASS_PREMIUM_XP_MARKER_BG_COLOR_START" },
  { key: "GAMEPASS_PREMIUM_XP_MARKER_BG_COLOR_END" },
  { key: "GAMEPASS_PREMIUM_XP_MARKER_BORDER_COLOR" },
  { key: "GAME_PASS_THUMB_COLOR" },
  { key: "GAME_PASS_THUMB_COLOR_2" },
  { key: "GAMEPASS_SLIDER_GRAD_COLOR" },
  // "PREMINUM" mirrors the misspelled key in the colours API (colorsPalette.json); do not "fix".
  { key: "GAMEPASS_PREMINUM_SLIDER_GRAD_COLOR" },
  { key: "GAMEPASS_COMPLETED_TASK_BG_GRAD" },
  { key: "GAMEPASS_GOLD_GRADIENT" },
  { key: "GAMEPASS_GOLD_REVERSE_GRADIENT" },
  { key: "GAMEPASS_DARK_GRADIENT" },
  // { key: "GAMEPASS_SPIN_GRADIENT" },
  { key: "GAME_PASS_PROGRESS_GRADIENT" },
  { key: "GAME_PASS_LEAGUE_ORDER_GRADIENT" },
];

/**
 * Turns a palette key into a human-readable row title, dropping the leading
 * Game Pass prefix (the whole section is Game Pass) and title-casing the rest.
 * e.g. `GAMEPASS_ACTIVE_COLOR` → "Active Color", `PRIMARY_BUTTON` → "Primary
 * Button", `GAME_PASS_THUMB_COLOR_2` → "Thumb Color 2".
 */
export const humanizeColorKey = (key: string): string =>
  key
    .replace(/^GAME_?PASS_/i, "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Converts an `rgb()/rgba()` palette value into an uppercase hex string for the
 * hex field + swatch seed. Adds the alpha byte only when the colour is
 * translucent. Values that are already hex (or unparseable) are returned
 * uppercased / untouched.
 */
export const rgbaToHex = (value: string): string => {
  if (!value) return "";

  const match = /rgba?\(([^)]+)\)/i.exec(value);
  if (!match) {
    return value.startsWith("#") ? value.toUpperCase() : value;
  }

  const parts = match[1].split(",").map((part) => part.trim());
  const [r, g, b] = parts.map(Number);
  if ([r, g, b].some((channel) => Number.isNaN(channel))) {
    return value;
  }

  const alpha = parts[3] === undefined ? 1 : Number(parts[3]);
  const toHex = (channel: number) =>
    Math.round(channel).toString(16).padStart(2, "0");

  const rgbHex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  const alphaHex = alpha < 1 ? toHex(Math.round(alpha * 255)) : "";
  return (rgbHex + alphaHex).toUpperCase();
};

/** A single colour row resolved against a palette: its title and colour stops. */
export type GamePassColorRow = {
  key: string;
  label: string;
  stops: string[];
};

export type ColorPalette = Record<string, string | string[] | undefined>;

/**
 * Upper bound on the number of indexed gradient stops read from the live
 * palette (`--COLOR-KEY_0`…`--COLOR-KEY_{n}`). Comfortably above any real
 * gradient while capping the scan so a malformed palette can't run the
 * discovery loop unbounded.
 */
const MAX_GRADIENT_STOPS = 32;

/**
 * Resolves the *live* brand palette the app is actually themed with, rather than
 * the bundled trivelta defaults.
 *
 * At startup `src/index.js` fetches the brand's `common_assets/colors.json` and
 * `applyColorVariables` writes every token onto `document.documentElement` as a
 * `--COLOR-*` custom property (single colours as `--COLOR-KEY`, gradient stops as
 * `--COLOR-KEY_0`, `--COLOR-KEY_1`, …). Reading those computed variables back is
 * what keeps the Style editor + preview in sync with the same palette the rest
 * of the PAM (and trivelta-gamepass-frontend) renders — e.g. a brand whose
 * PRIMARY is `rgba(14, 200, 237, 1)` shows cyan here, not the trivelta orange.
 *
 * Any token missing from the live palette (or when there's no DOM, e.g. in unit
 * tests) falls back to {@link AppColors} so the editor always has a value.
 */
export const resolveGamePassPalette = (
  fallback: ColorPalette = AppColors as ColorPalette,
): ColorPalette => {
  if (typeof document === "undefined" || !document.documentElement) {
    return fallback;
  }

  const root = document.documentElement;
  const computed = getComputedStyle(root);
  // Prefer the computed value (honours the cascade); fall back to the inline
  // property `applyColorVariables` writes directly onto `:root`, since some
  // engines (and jsdom) don't surface custom properties through getComputedStyle.
  const readVar = (name: string): string =>
    (computed.getPropertyValue(name) || root.style.getPropertyValue(name)).trim();

  const palette: ColorPalette = {};
  GAME_PASS_COLOR_KEYS.forEach(({ key }) => {
    const single = readVar(`--COLOR-${key}`);
    if (single) {
      palette[key] = single;
      return;
    }

    // Gradient tokens are emitted as contiguous indexed vars (`_0`, `_1`, …).
    // Capped so a malformed palette emitting an unbounded run of indexed vars
    // can't spin this loop thousands of times on every mount; real gradients
    // have only a handful of stops, well under this ceiling.
    const stops: string[] = [];
    for (let index = 0; index < MAX_GRADIENT_STOPS; index += 1) {
      const stop = readVar(`--COLOR-${key}_${index}`);
      if (!stop) break;
      stops.push(stop);
    }
    if (stops.length > 0) {
      palette[key] = stops;
      return;
    }

    const fallbackValue = fallback?.[key];
    if (fallbackValue !== undefined) palette[key] = fallbackValue;
  });

  return palette;
};

/**
 * Resolves the configured Game Pass colour keys against a palette (the colours
 * API response, or the bundled trivelta defaults). Gradient values keep all
 * their stops so the row can render one swatch + hex field per stop.
 */
export const buildGamePassColorRows = (
  palette: ColorPalette,
): GamePassColorRow[] =>
  GAME_PASS_COLOR_KEYS.map(({ key, label }) => {
    const value = palette?.[key];

    let stops: string[] = [];
    if (Array.isArray(value)) {
      stops = value;
    } else if (value) {
      stops = [value];
    }

    return {
      key,
      label: label ?? humanizeColorKey(key),
      stops,
    };
  });

/** Stable id for a single colour stop in the editable colours map. */
export const stopId = (rowKey: string, index: number): string =>
  `${rowKey}-${index}`;

/**
 * Seeds the editable colours map from a set of rows, keyed by {@link stopId}.
 * Single-colour tokens get one entry (`KEY-0`); gradient tokens get one entry
 * per stop (`KEY-0`, `KEY-1`, …). Values are normalised to hex so they drop
 * straight into the swatch + hex field.
 */
export const seedColors = (rows: GamePassColorRow[]): Record<string, string> => {
  const seed: Record<string, string> = {};
  rows.forEach((row) => {
    const stops = row.stops.length > 0 ? row.stops : [""];
    stops.forEach((stop, index) => {
      seed[stopId(row.key, index)] = rgbaToHex(stop);
    });
  });
  return seed;
};

/**
 * Overlays a saved theme's colours onto an already-seeded colours map.
 *
 * The editable colours map is keyed by {@link stopId} (`KEY-0`, `KEY-1`, …).
 * The theme document stores free-form string keys, so we accept both
 * conventions: a key that is already a stop id (`PRIMARY-0`) sets that stop
 * directly, while a bare token key (`PRIMARY`) targets the token's first stop
 * (`PRIMARY-0`) — the common single-colour case. Values are normalised to hex
 * so they drop straight into the swatch + hex field.
 *
 * Only keys that exist in the seed are applied; unknown theme keys and
 * non-string / empty values are ignored, so a stale or unexpected theme can
 * never blank out a swatch or inject a row the editor doesn't own. Any token
 * the theme doesn't set keeps its seeded default — that's the fallback that
 * makes an empty theme render identically to today.
 */
export const applyThemeColorsToSeed = (
  seed: Record<string, string>,
  themeColors?: Record<string, string> | null,
): Record<string, string> => {
  if (!themeColors) return seed;

  const next = { ...seed };
  Object.entries(themeColors).forEach(([key, value]) => {
    if (typeof value !== "string" || !value) return;

    const hex = rgbaToHex(value);
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = hex;
    } else if (Object.prototype.hasOwnProperty.call(next, `${key}-0`)) {
      next[`${key}-0`] = hex;
    }
  });

  return next;
};

/** Normalise a hex value for comparison (trim + upper-case). */
const normaliseHex = (value?: string): string => (value ?? "").trim().toUpperCase();

/**
 * The theme-document key a colour stop is persisted under.
 *
 * Mirrors what {@link applyThemeColorsToSeed} reads back so a saved value
 * round-trips: single-colour tokens use the bare key (`PRIMARY`), gradient
 * stops use the indexed stop id (`GAMEPASS_GOLD_GRADIENT-0`). `isGradient` is
 * derived from the row having more than one stop.
 */
export const themeColorKey = (
  rowKey: string,
  index: number,
  isGradient: boolean,
): string => (isGradient ? stopId(rowKey, index) : rowKey);

/**
 * Builds the `colors` merge-patch body for the theme API from the editor state.
 *
 * Only *dirty* stops (current value differs from the loaded baseline) are
 * emitted, so an unchanged save is a no-op and concurrent admins editing
 * different keys don't clobber each other. Per dirty stop:
 *   - reset back to the palette default → `null` (removes the override)
 *   - a valid custom hex → that hex (sets/overwrites the override)
 *   - an invalid / partial hex → skipped, so a half-typed value never 422s
 *
 * `baseline` is the persisted theme overlaid on the palette (the editor's state
 * on load / after the last save); `defaults` is the bare palette seed used to
 * detect a reset-to-default. All comparisons are case-insensitive.
 */
export const buildThemeColorsPatch = (
  rows: GamePassColorRow[],
  current: Record<string, string>,
  baseline: Record<string, string>,
  defaults: Record<string, string>,
): Record<string, string | null> => {
  const patch: Record<string, string | null> = {};

  rows.forEach((row) => {
    const stops = row.stops.length > 0 ? row.stops : [""];
    const isGradient = stops.length > 1;

    stops.forEach((_stop, index) => {
      const sid = stopId(row.key, index);
      const cur = normaliseHex(current[sid]);
      const base = normaliseHex(baseline[sid]);
      if (cur === base) return;

      const key = themeColorKey(row.key, index, isGradient);
      if (cur === normaliseHex(defaults[sid])) {
        patch[key] = null;
        return;
      }
      if (isValidHex(cur)) {
        patch[key] = cur;
      }
    });
  });

  return patch;
};

/**
 * Turns the editable colours map into a CSS-variable override object that can
 * be applied (inline) to the live preview's root element. Each filled token is
 * emitted under the same `--COLOR-*` name `applyColorVariables`
 * ({@link file://src/utils/colorUtils.js}) uses globally — single colours as
 * `--COLOR-KEY`, gradient stops as `--COLOR-KEY_0`, `--COLOR-KEY_1`, … — so the
 * preview's CSS (which reads those variables) reflects edits live, while
 * untouched tokens fall through to the document-level palette. Empty / invalid
 * swatches are skipped so they keep the global default rather than blanking
 * out.
 */
export const buildColorCssVariables = (
  rows: GamePassColorRow[],
  colors: Record<string, string>,
): Record<string, string> => {
  const vars: Record<string, string> = {};
  rows.forEach((row) => {
    const stops = row.stops.length > 0 ? row.stops : [""];
    const isGradient = stops.length > 1;
    stops.forEach((_stop, index) => {
      const value = colors[stopId(row.key, index)];
      if (!value || !isValidHex(value)) return;
      const name = isGradient
        ? `--COLOR-${row.key}_${index}`
        : `--COLOR-${row.key}`;
      vars[name] = value;
    });
  });
  return vars;
};
